import React, { useState } from 'react'
import {
  Box, Drawer, IconButton, Typography, Divider, Chip, Accordion,
  AccordionSummary, AccordionDetails, Tooltip,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { PM } from './promotionsTheme'

const STEP_NAMES = ['Type', 'Trigger', 'Conditions', 'Reward', 'Limits', 'Preview', 'Publish']

const PROMO_TYPE_HELP = [
  {
    label: '$ Off Order',
    value: 'ORDER_AMOUNT_OFF',
    color: '#16a34a',
    desc: 'Deducts a fixed dollar amount from the entire order subtotal.',
    example: 'Spend $40, get $5 off your order.',
    setup: 'Set a Minimum Spend condition (e.g. $40) + an Amount Off reward (e.g. $5).',
    tip: 'Great for increasing average order value.',
  },
  {
    label: '% Off Order',
    value: 'ORDER_PERCENT_OFF',
    color: '#16a34a',
    desc: 'Applies a percentage discount to the whole order subtotal.',
    example: 'Get 15% off any order over $30.',
    setup: 'Set a Minimum Spend condition + a Percent Off reward. Use Max Discount Cap to limit exposure.',
    tip: 'Use a Max Discount Cap to avoid losing too much on large orders.',
  },
  {
    label: '% Off Items',
    value: 'ITEM_PERCENT_OFF',
    color: '#7c3aed',
    desc: 'Applies a percentage discount to specific items or a category.',
    example: '20% off all Burgers.',
    setup: 'Add a Required Categories or Required Items condition, then add a Percent Off reward.',
    tip: 'Best for promoting a specific menu section like "Lunch Specials".',
  },
  {
    label: '$ Off Items',
    value: 'ITEM_AMOUNT_OFF',
    color: '#7c3aed',
    desc: 'Deducts a fixed amount from specific items or a category.',
    example: '$2 off Large Fries.',
    setup: 'Add a Required Items condition (select the item), then add an Amount Off reward.',
    tip: 'Works well for single item promotions like a side dish or drink.',
  },
  {
    label: 'Buy 1 Get 1 Free',
    value: 'BOGO',
    color: '#dc2626',
    desc: 'Customer buys one item and gets the cheapest eligible item free.',
    example: 'Buy any pizza, get the cheapest one free.',
    setup: 'No special conditions needed. Add a Cheapest Item Free reward. Optionally restrict to a category.',
    tip: 'Optionally restrict to a category (e.g. "Pizzas only") in the reward step.',
  },
  {
    label: 'Buy X Get Y% Off',
    value: 'BUY_X_GET_Y_PERCENT_OFF',
    color: '#ea580c',
    desc: 'Customer buys a set quantity or spends a threshold, then gets a % off specific items.',
    example: 'Buy 2 entrees, get an appetizer 50% off.',
    setup: 'Set a Required Qty condition (e.g. 2 items), then add a Percent Off reward and pick the discounted item/category.',
    tip: 'Specify the "items that get the discount" in the Reward step — leave blank for the whole cart.',
  },
  {
    label: 'Buy X Save $',
    value: 'BUY_X_SAVE_AMOUNT',
    color: '#ea580c',
    desc: 'Customer buys a set number of items and saves a fixed dollar amount.',
    example: 'Buy 2 combos, save $5.',
    setup: 'Set a Required Qty or Required Items condition, then add an Amount Off reward.',
    tip: 'Pair with a Required Items condition to target specific combos.',
  },
  {
    label: 'Free Item',
    value: 'FREE_ITEM_ON_THRESHOLD',
    color: '#0891b2',
    desc: 'Customer gets a specific free item when they reach a spend threshold.',
    example: 'Spend $50, get a free drink.',
    setup: 'Add a Minimum Spend condition, then add a Free Item reward and choose the specific item or category.',
    tip: 'In the reward step, pick "Any item from category" to let customers choose their free item.',
  },
  {
    label: 'Free Delivery',
    value: 'FREE_DELIVERY_ON_THRESHOLD',
    color: '#0891b2',
    desc: 'Waives the delivery fee when the order meets a minimum spend.',
    example: 'Free delivery on orders over $25.',
    setup: 'Add a Minimum Spend condition, then add a Free Delivery reward.',
    tip: 'Set the Channel to "Delivery only" in Conditions so it doesn\'t apply to pickup orders.',
  },
  {
    label: 'Loyalty Reward',
    value: 'LOYALTY_REWARD',
    color: '#ca8a04',
    desc: 'Rewards customers after they accumulate a total spend. Generates a voucher for their next order.',
    example: 'After spending $100 total, get a $10 voucher.',
    setup: 'Add a Minimum Spend condition (cumulative), then add a Generate Voucher reward with the voucher amount and validity days.',
    tip: 'Set validity days wisely — 30 days encourages quick re-orders.',
  },
]

const STEP_HELP = [
  {
    step: 0,
    title: 'Step 1 — Choose a Type',
    summary: 'Pick the promotion mechanic that best fits your goal.',
    points: [
      'Each type defines how the discount works (fixed $, %, free item, etc.)',
      'You can only pick one type per promotion.',
      'The type you choose determines which reward options appear later.',
      'Use "$ Off Order" or "% Off Order" for general cart discounts.',
      'Use item/category types for targeted menu promotions.',
    ],
    warning: null,
  },
  {
    step: 1,
    title: 'Step 2 — Set the Trigger',
    summary: 'Decide how customers activate this promotion.',
    points: [
      '"Apply at Cart" — deal shows automatically at checkout, no code needed.',
      '"Coupon Code" — customer must type the code you create at checkout.',
      'Coupon codes are always converted to uppercase automatically.',
      'Give the promotion a clear, customer-facing name (e.g. "Weekend 15% Off").',
      'The description is optional but shows on the Deals page.',
    ],
    warning: 'Coupon codes must be unique. Two active promotions cannot share the same code.',
  },
  {
    step: 2,
    title: 'Step 3 — Define Conditions',
    summary: 'Set the rules a cart must meet before the discount applies.',
    points: [
      'All conditions must be met at the same time.',
      '"Minimum Spend" — cart subtotal must be at least this amount.',
      '"Required Items" — specific menu items must be in the cart.',
      '"Required Categories" — customer must add items from selected categories.',
      '"Minimum Quantity" — cart must have at least this many items.',
      '"Customer Targeting" — choose All, New, Existing, or Lapsed customers.',
      '"Channel" — restrict to Delivery, Pickup, or both.',
    ],
    warning: 'Having too many conditions makes the promo harder to trigger. Start simple.',
  },
  {
    step: 3,
    title: 'Step 4 — Define the Reward',
    summary: 'The actual benefit the customer receives when conditions are met.',
    points: [
      '"$ Off" — subtracts a flat dollar amount from the subtotal.',
      '"% Off" — subtracts a percentage from the subtotal.',
      '"Free Item" — adds a specific item (or one from a category) for free.',
      '"Cheapest Item Free" — automatically finds and frees the cheapest eligible item.',
      '"Free Delivery" — removes the delivery fee completely.',
      '"Generate Voucher" — creates a credit voucher for the customer\'s next order.',
      'The system only shows reward types that match your chosen promotion type.',
    ],
    warning: 'You must add at least one reward or you cannot advance past this step.',
  },
  {
    step: 4,
    title: 'Step 5 — Set Limits & Schedule',
    summary: 'Control when, how often, and how much this promotion can be used.',
    points: [
      '"Start / End Date" — promotion only activates within this window.',
      '"Max Total Redemptions" — e.g. first 100 orders only. Leave blank for unlimited.',
      '"Max Per Customer" — prevents one customer from using it multiple times.',
      '"Max Discount Cap" — hard ceiling on the discount (useful for % off promotions).',
      '"Priority" — if multiple promos apply, higher priority wins.',
      '"Stackable" — allows this promo to combine with another active promotion.',
      '"Active Days" — run only on specific days (e.g. Fri, Sat, Sun).',
      '"Time Window" — run only during certain hours (e.g. 11am–2pm for lunch deals).',
    ],
    warning: 'Leave all day/time fields blank to run the promotion at any time.',
  },
  {
    step: 5,
    title: 'Step 6 — Preview',
    summary: 'Simulate a real cart to verify the promotion works as expected before going live.',
    points: [
      'Click "Save Draft" first — preview requires a saved promotion ID.',
      'Add cart items with their name, category, price, and quantity.',
      'Enter a delivery fee to test Free Delivery promotions.',
      'If the promotion is coupon-based, enter the code in the Promo Code field.',
      'Click "Simulate Cart" to see whether the promo would apply.',
      'The result shows original total, discount, and final total.',
      'If it shows "would NOT apply", check your conditions — something isn\'t being met.',
    ],
    warning: 'Always preview before publishing, especially for complex multi-condition promos.',
  },
  {
    step: 6,
    title: 'Step 7 — Review & Publish',
    summary: 'Final review of all settings before making the promotion live.',
    points: [
      'Review all fields — type, name, trigger, targeting, dates, and limits.',
      'Click "Publish Now" to make the promotion immediately active.',
      'Published promotions appear on the customer Deals page right away.',
      'You can still edit and pause a live promotion from the Promotions list.',
      '"Save Draft" keeps it saved but not live — useful for scheduling future promotions.',
    ],
    warning: 'Publishing is immediate. Make sure your start date and conditions are correct.',
  },
]

function TypeCard({ type }) {
  return (
    <Box sx={{
      border: `1px solid ${PM.border}`, borderRadius: '12px', p: 1.75, mb: 1.5, bgcolor: '#FFFFFF',
      borderLeft: `3px solid ${PM.amber}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Chip label={type.label} size="small" sx={{ bgcolor: PM.chipBg, color: PM.label, fontWeight: 700, fontSize: 13, borderRadius: '8px' }} />
      </Box>
      <Typography variant="caption" sx={{ display: 'block', fontSize: 13, color: PM.body, mb: 0.5 }}>{type.desc}</Typography>
      <Typography variant="caption" sx={{ display: 'block', fontSize: 13, color: PM.secondary, fontStyle: 'italic', mb: 0.5 }}>
        Example: {type.example}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.5 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 15, color: PM.secondary, mt: '1px', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: PM.body, fontSize: 13 }}>{type.setup}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.5 }}>
        <LightbulbOutlinedIcon sx={{ fontSize: 15, color: PM.amberIcon, mt: '1px', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: PM.amberText, fontSize: 13 }}>{type.tip}</Typography>
      </Box>
    </Box>
  )
}

export default function PromotionHelp({ currentStep }) {
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(null)

  const handleOpen = () => {
    setExpanded(`step-${currentStep}`)
    setOpen(true)
  }

  return (
    <>
      {/* Floating help button */}
      <Tooltip title="How to use this wizard" placement="left">
        <IconButton
          onClick={handleOpen}
          sx={{
            position: 'fixed', bottom: 32, right: 32, zIndex: 1300,
            bgcolor: PM.text, color: '#fff', width: 52, height: 52,
            boxShadow: '0 8px 20px rgba(15,23,42,0.25)',
            '&:hover': { bgcolor: PM.primaryHover },
            '&.Mui-focusVisible': { outline: `2px solid ${PM.focus}`, outlineOffset: 2 },
          }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 420 }, bgcolor: PM.page, color: PM.text, fontFamily: PM.font } }}
      >
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, bgcolor: PM.text, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: 20, color: '#fff' }}>Promotions Guide</Typography>
            <Typography variant="caption" sx={{ fontSize: 13, color: PM.bannerSub }}>
              Currently on: Step {currentStep + 1} — {STEP_NAMES[currentStep]}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff', width: 44, height: 44, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1, pb: 4 }}>
          {/* Current step callout */}
          <Box sx={{ mx: 2, mt: 2, mb: 1, p: 2, bgcolor: PM.paleAmber, borderRadius: '12px', border: `1px solid ${PM.noteBorder}` }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12, color: PM.amberText, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              You are on this step
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: PM.text, fontSize: 15, mt: 0.25 }}>
              {STEP_HELP[currentStep]?.title}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 13, color: PM.body }}>{STEP_HELP[currentStep]?.summary}</Typography>
          </Box>

          {/* Step-by-step guide */}
          <Box sx={{ px: 2, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: PM.text, fontSize: 15, mb: 0.5, mt: 1.5 }}>
              Step-by-Step Guide
            </Typography>
          </Box>
          {STEP_HELP.map((sh, idx) => (
            <Accordion
              key={idx}
              expanded={expanded === `step-${idx}`}
              onChange={(_, isExp) => setExpanded(isExp ? `step-${idx}` : null)}
              elevation={0}
              disableGutters
              sx={{
                mx: 2, mb: 0.75, borderRadius: '10px !important',
                border: expanded === `step-${idx}` ? `1.5px solid ${PM.text}` : `1px solid ${PM.border}`,
                bgcolor: '#fff',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: PM.secondary }} />} sx={{ py: 0.5, minHeight: 44, '&.Mui-focusVisible': { bgcolor: PM.page } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%',
                    bgcolor: idx === currentStep ? PM.amber : PM.chipBg,
                    color: idx === currentStep ? PM.text : PM.secondary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {idx + 1}
                  </Box>
                  <Typography variant="body2" fontWeight={idx === currentStep ? 700 : 500} sx={{ fontSize: 15, color: PM.text }}>
                    {STEP_NAMES[idx]}
                  </Typography>
                  {idx === currentStep && (
                    <Chip label="Current" size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#FFF1D6', color: PM.amberText, fontWeight: 700, borderRadius: '999px' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontSize: 13, color: PM.secondary }}>{sh.summary}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                  {sh.points.map((pt, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 15, color: PM.secondary, mt: '2px', flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: PM.body, fontSize: 13, lineHeight: 1.5 }}>{pt}</Typography>
                    </Box>
                  ))}
                </Box>
                {sh.warning && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, p: 1.25, bgcolor: PM.paleAmber, borderRadius: '10px', border: `1px solid ${PM.noteBorder}` }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: PM.amberIcon, mt: '1px', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: PM.amberText, fontSize: 13, lineHeight: 1.45 }}>{sh.warning}</Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}

          <Divider sx={{ mx: 2, my: 2, borderColor: PM.border }} />

          {/* Promotion types reference */}
          <Box sx={{ px: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: PM.text, fontSize: 15, mb: 1 }}>
              All Promotion Types — Reference
            </Typography>
            {PROMO_TYPE_HELP.map(type => (
              <TypeCard key={type.value} type={type} />
            ))}
          </Box>
        </Box>
      </Drawer>
    </>
  )
}
